import React, {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useState,
} from "react";
import { SettingsApiTypes } from "@stellar/settings-api-client";
import { settingsApiClient } from "@api/settings-api-client";
import {
  pruneOrphanedColumns,
  getVisibleOrderedColumns,
  normalizeColumnOrders,
} from "@context-providers/table/column-preferences-utils";
import { useErrorContext } from "@context-providers/error-boundary/error-handling-context";

export interface UseColumnPreferencesOptions<K extends string> {
  /** Columns that must always be visible and placed last (e.g., actions) */
  nonConfigurableColumns?: K[];
  /** List of the default visible columns */
  defaultVisibleColumns?: K[];
}

interface ColumnPreferencesContextValue<K extends string> {
  /** Persists column preferences to backend API (batched call when menu closes) */
  updateColumnPreferences: (
    preferences: SettingsApiTypes.ColumnPreferenceMap
  ) => Promise<void>;

  /** Updates column preferences immediately (local state only, no API call) */
  updateColumnPreferencesLocally: (
    preferences: SettingsApiTypes.ColumnPreferenceMap
  ) => void;

  /** Current column preferences including visibility, order, and sort state */
  columnPreferences: SettingsApiTypes.ColumnPreferenceMap | undefined;

  /** Visible columns ordered by 'order', with always-visible columns appended last */
  visibleOrderedColumns: K[];

  /** Configurable columns for the visibility menu, ordered by 'order' from backend preferences */
  dataColumns: K[];
}

const ColumnPreferencesContext = createContext<
  ColumnPreferencesContextValue<string> | undefined
>(undefined);

/** Props for the ColumnPreferencesProvider component */
export type Props<K extends string> = {
  /** Unique key to identify the table (used for user preferences storage) */
  tableKey: string;
  /** Array of all possible column keys for the table */
  columns: K[];
  /** Optional configuration for non-configurable columns and default visible columns */
  options?: UseColumnPreferencesOptions<K>;
  /** React children to be rendered within the provider */
  children: ReactNode;
};

/**
 * Custom provider to manage column visibility state with persistence via user settings API.
 *
 * Features:
 * - Fetches table column preferences from user settings on mount
 * - Updates preferences when visibility changes
 * - Ensures "options" column is always visible
 */
export function ColumnPreferencesProvider<K extends string>({
  tableKey,
  columns,
  options,
  children,
}: Props<K>): JSX.Element {
  const [settingsData, setSettingsData] = useState<{
    value: SettingsApiTypes.SettingsValueByScope["USER"]["table_column_preferences"];
    modifiedAt: string;
  } | null>(null);

  const [currentColumnPreferences, setCurrentColumnPreferences] = useState<
    SettingsApiTypes.ColumnPreferenceMap | undefined
  >(undefined);

  const { handleErrorWithToast } = useErrorContext();

  // Columns that must always stay visible and be placed after data columns (e.g. options)
  const alwaysVisibleColumns = options?.nonConfigurableColumns ?? ([] as K[]);
  const alwaysVisibleSet = new Set(alwaysVisibleColumns);

  // Split between data columns and the non-configurable ones; build a unified set of valid columns
  const dataColumns = columns.filter((col) => !alwaysVisibleSet.has(col));
  const validColumnsSet = new Set<K>([...dataColumns, ...alwaysVisibleColumns]);

  /**
   * Fetch table column preferences from user settings API on mount.
   * If no preferences exist, create default with all columns visible.
   */
  useEffect(() => {
    async function fetchPreferences(): Promise<void> {
      try {
        const { value, modifiedAt } =
          await settingsApiClient.user.fetchUserSettings(
            "table_column_preferences"
          );

        setSettingsData({
          value,
          modifiedAt,
        });

        const tablePreferences = value[tableKey] ?? {};

        // Normalize preferences with two guarantees:
        // - every data column exists (new columns default to visible and appended after existing ones)
        // - non-configurable columns exist, stay visible, and are placed after data columns
        const mergedPreferences: SettingsApiTypes.ColumnPreferenceMap = {
          ...tablePreferences,
        };

        // Drop prefs that refer to columns that no longer exist in the table definition
        pruneOrphanedColumns(mergedPreferences, validColumnsSet);

        for (const [idx, column] of dataColumns.entries()) {
          if (!mergedPreferences[column]) {
            mergedPreferences[column] = {
              // eslint-disable-next-line @typescript-eslint/naming-convention
              visible: options?.defaultVisibleColumns
                ? options.defaultVisibleColumns.includes(column)
                : true,
              order: idx + 1,
              sortBy: null,
            };
          }
        }

        for (const col of alwaysVisibleColumns) {
          const idx = alwaysVisibleColumns.indexOf(col);
          mergedPreferences[col] = {
            // eslint-disable-next-line @typescript-eslint/naming-convention
            visible: true,
            order: dataColumns.length + idx + 1,
            sortBy: null,
          };
        }

        setCurrentColumnPreferences(mergedPreferences);
      } catch (error) {
        handleErrorWithToast({
          id: `fetch-table-column-preferences-${Date.now().toString()}`,
          title:
            "Failed to load your column preferences. Using default settings.",
          error,
        });
      }
    }
    void fetchPreferences();
    // Only tableKey should trigger this effect to prevent unnecessary re-fetches
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tableKey]);

  /**
   * Persists column preferences to user settings API.
   * Ensures "options" is always visible and merges with other tables' preferences.
   */
  async function updateColumnPreferences(
    newColumnPreferences: SettingsApiTypes.ColumnPreferenceMap
  ): Promise<void> {
    try {
      if (!settingsData) {
        handleErrorWithToast({
          id: `settings-data-table-column-preferences-${Date.now().toString()}`,
          title: "Unable to save column preferences. Please try again.",
          error: new Error("Settings data not loaded yet"),
        });
        return;
      }

      const fixedPreferences: SettingsApiTypes.ColumnPreferenceMap = {
        ...newColumnPreferences,
      };

      // Drop prefs for columns that no longer exist
      pruneOrphanedColumns(fixedPreferences, validColumnsSet);

      const normalizedPreferences = normalizeColumnOrders(
        fixedPreferences,
        alwaysVisibleColumns
      );

      // Merge with existing preferences from other tables
      const updatedPreferences = {
        ...settingsData.value,
        [tableKey]: normalizedPreferences,
      };

      const { value, modifiedAt } =
        await settingsApiClient.user.updateUserSettings(
          "table_column_preferences",
          {
            value: updatedPreferences,
            modifiedAt: settingsData.modifiedAt,
          }
        );
      setSettingsData({
        value,
        modifiedAt,
      });
      setCurrentColumnPreferences(value[tableKey]);
    } catch (error) {
      handleErrorWithToast({
        id: `update-table-column-preferences-${Date.now().toString()}`,
        title:
          "Failed to save column preferences. Your changes were not saved.",
        error,
      });
    }
  }

  const visibleOrderedColumns = getVisibleOrderedColumns(
    currentColumnPreferences,
    dataColumns,
    alwaysVisibleColumns,
    validColumnsSet,
    alwaysVisibleSet
  );

  const value: ColumnPreferencesContextValue<K> = {
    updateColumnPreferences,
    updateColumnPreferencesLocally: setCurrentColumnPreferences,
    columnPreferences: currentColumnPreferences,
    visibleOrderedColumns,
    dataColumns,
  };

  return (
    <ColumnPreferencesContext.Provider value={value}>
      {children}
    </ColumnPreferencesContext.Provider>
  );
}

export function useColumnPreferencesContext<
  K extends string
>(): ColumnPreferencesContextValue<K> {
  const context = useContext(ColumnPreferencesContext);
  if (!context) {
    throw new Error(
      "useColumnPreferencesContext must be used within a ColumnPreferencesProvider"
    );
  }
  return context as ColumnPreferencesContextValue<K>;
}
